import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { GraphLink, GraphNode, ComponentColors } from './types';
import { getDescendants, getTypeTier, getSymbol } from './utils/graphUtils';
import { ChevronRight, ChevronLeft, X, RotateCcw, Zap, Maximize2, List, Info, GitBranch, FileText, FileCheck, Search, Eye, EyeOff } from 'lucide-react';

interface DetailsPanelProps {
  selectedNodes: GraphNode[];
  onClose: () => void;
  nodes?: GraphNode[];
  links?: GraphLink[];
  onToggleType?: (nodeIds: string[], type: string) => void;
  onCollapseTypeAndBelow?: (nodeIds: string[], type: string) => void;
  expandedIds?: Set<string>;
  powerVisMode: 'none' | 'shadow' | 'halo' | 'bar';
  onPowerSelect?: (filter: { power: number; tier: number } | null) => void;
  onPropertySelect?: (key: string, value: any) => void;
  onLegendClick?: (type: string) => void;
  visibleNodeIds: Set<string>;
  onMultiNodeSelect?: (nodeIds: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  // New Control Props
  orientation: 'TD' | 'LR';
  onOrientationChange: (o: 'TD' | 'LR') => void;
  onPowerVisChange: (m: 'none' | 'shadow' | 'halo' | 'bar') => void;
  onExpandAuxiliary?: () => void;
  onCollapseAuxiliary?: () => void;
  shouldSwitchToDetails?: boolean;
  onNodeSelect?: (nodeId: string) => void;
}

const DetailsPanel: React.FC<DetailsPanelProps> = ({
  selectedNodes = [],
  onClose,
  nodes = [],
  links = [],
  onToggleType,
  onCollapseTypeAndBelow,
  expandedIds,
  powerVisMode,
  onPowerSelect,
  onPropertySelect,
  onLegendClick,
  visibleNodeIds,
  onMultiNodeSelect,
  isOpen,
  onToggle,
  orientation,
  onOrientationChange,
  onPowerVisChange,
  onExpandAuxiliary,
  onCollapseAuxiliary,
  shouldSwitchToDetails,
  onNodeSelect,
}) => {

  const [activeTab, setActiveTab] = useState<'legend' | 'details' | 'connections'>('legend');
  const [nodeSearchTerm, setNodeSearchTerm] = useState('');
  const [searchFocusCount, setSearchFocusCount] = useState(0); // increments on each focus to force re-render
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const isSearchFocused = searchFocusCount > 0;
  const searchRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Node search results
  const searchResults = useMemo(() => {
    if (!nodeSearchTerm.trim()) return [];
    const term = nodeSearchTerm.toLowerCase();
    return nodes
      .filter(n =>
        n.id.toLowerCase().includes(term) ||
        n.label.toLowerCase().includes(term) ||
        n.type.replace(/_/g, ' ').toLowerCase().includes(term)
      )
      .sort((a, b) => {
        // Exact ID matches first
        const aExact = a.id.toLowerCase() === term ? 0 : 1;
        const bExact = b.id.toLowerCase() === term ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        // Then by tier (lower tier = higher in hierarchy = first)
        if (a.tier !== b.tier) return a.tier - b.tier;
        // Then alphabetically by ID
        return a.id.localeCompare(b.id);
      })
      .slice(0, 50);
  }, [nodeSearchTerm, nodes]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocusCount(0);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-switch tabs based on selection (only when shouldSwitchToDetails is true)
  useEffect(() => {
    if (selectedNodes.length > 0 && shouldSwitchToDetails) {
      setActiveTab('details');
    } else if (selectedNodes.length === 0) {
      setActiveTab('legend');
    }
  }, [selectedNodes, shouldSwitchToDetails]);

  // Navigate to a page in the as-built viewer
  const navigateToAsBuiltPage = useCallback((page: number, nodeId?: string, nodeLabel?: string) => {
    window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
      detail: {
        nodeId: nodeId || `page-${page}`,
        nodeLabel: nodeLabel || `Page ${page}`,
        page,
        pageRefs: [{ label: nodeLabel || `Page ${page}`, page }]
      }
    }));
  }, []);

  // Get page reference from selected node
  const nodePageRef = useMemo(() => {
    if (selectedNodes.length !== 1) return null;
    const node = selectedNodes[0];
    const pref = node.details?.pref;
    if (pref === undefined || pref === null) return null;
    // Handle both { p: number } format and direct number
    if (typeof pref === 'object' && pref.p != null) {
      return pref.p;
    }
    if (typeof pref === 'number') {
      return pref;
    }
    return null;
  }, [selectedNodes]);

  // ... (Stats calculations remain the same) ...
  // 1. Calculate Electrical Hierarchy Stats (Downstream)
  const electricalStats = useMemo(() => {
    if (!selectedNodes.length || !links.length) return null;

    const counts: Record<string, number> = {};
    const firstIdOfType: Record<string, string> = {};

    selectedNodes.forEach(node => {
      const { nodeIds } = getDescendants(node.id, links);
      nodeIds.forEach(nid => {
        const n = nodes.find(gn => gn.id === nid);
        if (n) {
          counts[n.type] = (counts[n.type] || 0) + 1;
          if (!firstIdOfType[n.type]) firstIdOfType[n.type] = n.id;
        }
      });
    });

    return Object.entries(counts).sort((a, b) => {
      const tierA = getTypeTier(a[0]);
      const tierB = getTypeTier(b[0]);
      if (tierA !== tierB) return tierA - tierB;
      return b[1] - a[1];
    }).map(([type, count]) => ({ type, count, sampleId: firstIdOfType[type] }));
  }, [selectedNodes, links, nodes]);

  // 2. Calculate Path to Grid (upstream hierarchy with edges)
  interface PathStep {
    node: GraphNode;
    edge: GraphLink | null; // Edge connecting to the next node upstream (null for root)
    cumulativeLength: number; // Cumulative cable length from selected node
    cumulativePower: number; // Power at this node
  }

  const pathToGrid = useMemo(() => {
    if (!selectedNodes.length || selectedNodes.length > 1) return null;
    if (!nodes.length) return null;

    const selectedNode = selectedNodes[0];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const path: PathStep[] = [];

    // Get the node from nodeMap to ensure we have parentId (selectedNodes may not have it)
    let currentNode: GraphNode | undefined = nodeMap.get(selectedNode.id) || selectedNode;
    let cumulativeLength = 0;

    // Walk up from selected node to root (grid)
    while (currentNode) {
      const nodeId = currentNode.id;
      const parentId = currentNode.parentId;

      // Find the edge connecting to parent (non-COMM edge where current is source)
      let connectingEdge: GraphLink | null = null;

      if (parentId) {
        connectingEdge = links.find(l =>
          l.source === nodeId &&
          l.target === parentId &&
          l.details.signal !== 'COMM'
        ) || null;

        // Get cable length from edge configuration or properties
        if (connectingEdge) {
          const config = connectingEdge.details.configuration as Record<string, any> | undefined;
          // Prefer Length (ft) from configuration, convert to meters for cumulative
          const lengthFt = Number(config?.['Length (ft)'] || config?.['length_ft'] || 0);
          const lengthM = Number(connectingEdge.details.properties?.['Cable Length (m)'] ||
                                 connectingEdge.details.properties?.['Length'] ||
                                 connectingEdge.details.properties?.['cable_length'] || 0);
          // Use ft if available (convert to m for cumulative), otherwise use m directly
          const edgeLength = lengthFt > 0 ? lengthFt * 0.3048 : lengthM;
          cumulativeLength += edgeLength;
        }
      }

      path.push({
        node: currentNode,
        edge: connectingEdge,
        cumulativeLength,
        cumulativePower: currentNode.cumulativePower || 0
      });

      // Move to parent
      if (parentId) {
        currentNode = nodeMap.get(parentId);
      } else {
        currentNode = undefined;
      }
    }

    return path;
  }, [selectedNodes, nodes, links]);

  // 3. Calculate Power Distribution (Individual component powers for selected type(s))
  const powerDistribution = useMemo(() => {
    if (!selectedNodes.length || !nodes.length) return null;

    // Get unique types from selected nodes
    const selectedTypes = new Set(selectedNodes.map(n => n.type));

    // Find all nodes of the same type(s) as selected nodes
    const sameTypeNodes = nodes.filter(n => selectedTypes.has(n.type));

    // Group by cumulative power
    const powerGroups: Record<number, GraphNode[]> = {};
    sameTypeNodes.forEach(n => {
      if (n.cumulativePower !== undefined && n.cumulativePower > 0) {
        const powerKey = Math.round(n.cumulativePower); // Round to avoid floating point issues
        if (!powerGroups[powerKey]) {
          powerGroups[powerKey] = [];
        }
        powerGroups[powerKey].push(n);
      }
    });

    // Total count of filtered components (for percentage calculation)
    const totalFilteredCount = sameTypeNodes.filter(n =>
      n.cumulativePower !== undefined && n.cumulativePower > 0
    ).length;

    // Convert to array and sort by power descending
    return Object.entries(powerGroups)
      .map(([powerStr, groupNodes]) => {
        const power = parseInt(powerStr);
        const count = groupNodes.length;
        // Percentage is count / total filtered count (e.g., 2 of 5 inverters = 40%)
        const percentage = totalFilteredCount > 0 ? (count / totalFilteredCount) * 100 : 0;
        return {
          power,
          count,
          percentage,
          nodeIds: groupNodes.map(n => n.id) // Store all node IDs for selection
        };
      })
      .sort((a, b) => b.power - a.power);
  }, [selectedNodes, nodes]);

  // STC Power Distribution - groups by aggregate_specs.stc_power_w
  const stcPowerDistribution = useMemo(() => {
    if (!selectedNodes.length || !nodes.length) return null;

    const selectedTypes = new Set(selectedNodes.map(n => n.type));
    const sameTypeNodes = nodes.filter(n => selectedTypes.has(n.type));

    const stcGroups: Record<number, GraphNode[]> = {};
    sameTypeNodes.forEach(n => {
      const stcPower = (n.details.configuration?.aggregate_specs as any)?.stc_power_w;
      if (stcPower !== undefined && stcPower > 0) {
        const key = Math.round(stcPower);
        if (!stcGroups[key]) stcGroups[key] = [];
        stcGroups[key].push(n);
      }
    });

    const totalCount = sameTypeNodes.filter(n =>
      (n.details.configuration?.aggregate_specs as any)?.stc_power_w !== undefined
    ).length;

    if (Object.keys(stcGroups).length === 0) return null;

    return Object.entries(stcGroups)
      .map(([stcStr, groupNodes]) => ({
        stcPower: parseInt(stcStr),
        count: groupNodes.length,
        percentage: totalCount > 0 ? (groupNodes.length / totalCount) * 100 : 0,
        nodeIds: groupNodes.map(n => n.id)
      }))
      .sort((a, b) => b.stcPower - a.stcPower);
  }, [selectedNodes, nodes]);

  // Module Count Distribution - groups by aggregate_specs.module_count
  const moduleCountDistribution = useMemo(() => {
    if (!selectedNodes.length || !nodes.length) return null;

    const selectedTypes = new Set(selectedNodes.map(n => n.type));
    const sameTypeNodes = nodes.filter(n => selectedTypes.has(n.type));

    const moduleGroups: Record<number, GraphNode[]> = {};
    sameTypeNodes.forEach(n => {
      const moduleCount = (n.details.configuration?.aggregate_specs as any)?.module_count;
      if (moduleCount !== undefined && moduleCount > 0) {
        if (!moduleGroups[moduleCount]) moduleGroups[moduleCount] = [];
        moduleGroups[moduleCount].push(n);
      }
    });

    const totalCount = sameTypeNodes.filter(n =>
      (n.details.configuration?.aggregate_specs as any)?.module_count !== undefined
    ).length;

    if (Object.keys(moduleGroups).length === 0) return null;

    return Object.entries(moduleGroups)
      .map(([countStr, groupNodes]) => ({
        moduleCount: parseInt(countStr),
        count: groupNodes.length,
        percentage: totalCount > 0 ? (groupNodes.length / totalCount) * 100 : 0,
        nodeIds: groupNodes.map(n => n.id)
      }))
      .sort((a, b) => b.moduleCount - a.moduleCount);
  }, [selectedNodes, nodes]);

  // Check visibility state for toggles
  const checkVisible = (type: string) => {
    if (!selectedNodes.length || !nodes.length || !visibleNodeIds) return false;
    const relevantDescendants: GraphNode[] = [];
    selectedNodes.forEach(node => {
      const { nodeIds } = getDescendants(node.id, links);
      nodeIds.forEach(nid => {
        const n = nodes.find(gn => gn.id === nid);
        if (n && n.type === type) {
          relevantDescendants.push(n);
        }
      });
    });
    if (relevantDescendants.length === 0) return false;
    return relevantDescendants.every(n => visibleNodeIds.has(n.id));
  };

  // Helper to render property chips
  const renderProperties = (objKey: 'properties' | 'configuration') => {
    if (!selectedNodes.length) return null;

    // Keys to skip (aggregate_specs is shown separately in Power Flow Aggregates)
    const skipKeys = objKey === 'configuration' ? ['aggregate_specs'] : [];

    // 1. Collect all keys present in any selected node, filtering out objects and skipped keys
    const allKeys = new Set<string>();
    selectedNodes.forEach(n => {
      if (n.details[objKey]) {
        Object.keys(n.details[objKey]!).forEach(k => {
          // Skip specified keys and object values
          if (skipKeys.includes(k)) return;
          const value = n.details[objKey]![k];
          if (typeof value === 'object' && value !== null) return;
          allKeys.add(k);
        });
      }
    });

    // Always show the section with "None" if empty
    return (
      <div>
        <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">
          {objKey === 'properties' ? 'Properties' : 'Configuration'}
        </label>
        {allKeys.size === 0 ? (
          <div className="mt-1 eg-text-muted text-sm italic">None</div>
        ) : (
          <div className="mt-2 bg-[var(--color-surface)] rounded-md p-3 space-y-2 border border-[var(--color-border)]">
            {Array.from(allKeys).sort().map(key => {
              // Collect all values for this key
              const values = new Set<string>();
              selectedNodes.forEach(n => {
                if (n.details[objKey] && n.details[objKey]![key] !== undefined) {
                  const value = n.details[objKey]![key];
                  // Skip objects
                  if (typeof value === 'object' && value !== null) return;
                  values.add(String(value));
                }
              });

              return (
                <div key={key} className="flex flex-col border-b border-[var(--color-border)] last:border-0 pb-2 last:pb-0">
                  <span className="text-xs eg-text-muted mb-1">{key}</span>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(values).sort().map(val => (
                      <button
                        key={val}
                        onClick={() => onPropertySelect && onPropertySelect(key, isNaN(Number(val)) ? val : Number(val))}
                        className="px-2 py-0.5 rounded text-xs font-mono bg-[var(--color-surface-hover)] eg-text-primary hover:bg-emerald-600 hover:text-white transition-colors border border-[var(--color-border)] hover:border-emerald-500"
                        title={`Select all with ${key} = ${val}`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // 3. Get unique node types from the graph, separated into power and auxiliary
  const { powerComponents, auxiliaryComponents } = useMemo(() => {
    const typeData = new Map<string, { count: number; tier: number; isAncillary: boolean }>();

    nodes.forEach(n => {
      const existing = typeData.get(n.type);
      if (existing) {
        existing.count++;
      } else {
        typeData.set(n.type, {
          count: 1,
          tier: n.tier ?? 999,
          isAncillary: n.isAncillary ?? false
        });
      }
    });

    const allTypes = Array.from(typeData.entries()).map(([type, data]) => ({
      type,
      count: data.count,
      tier: data.tier,
      isAncillary: data.isAncillary,
      color: ComponentColors[type as keyof typeof ComponentColors] || '#9CA3AF'
    }));

    // Sort by tier (power flow position)
    const sortByTier = (a: typeof allTypes[0], b: typeof allTypes[0]) => a.tier - b.tier;

    return {
      powerComponents: allTypes.filter(t => !t.isAncillary).sort(sortByTier),
      auxiliaryComponents: allTypes.filter(t => t.isAncillary).sort(sortByTier)
    };
  }, [nodes]);


  // 4. Get unique edge types
  const edgeTypes = useMemo(() => {
    const types = new Set<string>();
    links.forEach(l => {
      if (l.details?.signal) types.add(l.details.signal);
    });
    return Array.from(types);
  }, [links]);

  // 5. Calculate counts of selected nodes by type (Always calculated to avoid hook violation)
  const selectedTypeCounts = useMemo(() => {
    if (selectedNodes.length <= 1) return null;
    const counts: Record<string, number> = {};
    selectedNodes.forEach(n => {
      counts[n.type] = (counts[n.type] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1]) // Sort by count desc
      .map(([type, count]) => ({ type, count }));
  }, [selectedNodes]);


  // --- Render Content Functions ---

  const renderLegend = () => {
    const formatTypeName = (type: string) => {
      return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    const getEdgeColor = (signal: string) => {
      if (signal === 'AC') return '#ef4444';
      if (signal === 'DC') return '#10b981';
      if (signal === 'COMM') return '#3b82f6';
      return '#9CA3AF';
    };

    // Filter to select nodes of target type (only for types with ≤100 items)
    const handleFilterClick = (targetType: string) => {
      if (!onMultiNodeSelect) return;

      const nodesOfType = nodes.filter(n => n.type === targetType);
      // Select all nodes of this type
      onMultiNodeSelect(nodesOfType.map(n => n.id));
    };

    const renderComponentList = (components: typeof powerComponents) => (
      <div className="space-y-1.5">
        {components.map(({ type, count, color }) => {
          const isFilterable = count <= 100;

          return (
            <div
              key={type}
              className={`flex items-center justify-between px-2 py-1.5 rounded transition-all group ${isFilterable ? 'hover:bg-[var(--color-surface-hover)] cursor-pointer' : ''}`}
              onClick={isFilterable ? () => handleFilterClick(type) : undefined}
              title={isFilterable ? `Click to select all ${count} ${formatTypeName(type)}` : `Too many items to filter (${count})`}
            >
              <div className="flex items-center flex-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="-20 -20 40 40"
                  className={`mr-2 flex-shrink-0 transition-transform ${isFilterable ? 'group-hover:scale-125' : ''}`}
                >
                  <path
                    d={getSymbol(type)}
                    fill={color}
                    stroke={color}
                    strokeWidth="2"
                    className={`transition-opacity ${isFilterable ? 'opacity-80 group-hover:opacity-100' : 'opacity-60'}`}
                  />
                </svg>
                <span className={`text-xs ${isFilterable ? 'eg-text-secondary group-hover:eg-text-primary' : 'eg-text-muted'}`}>
                  {formatTypeName(type)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${isFilterable ? 'eg-text-muted group-hover:text-emerald-400' : 'eg-text-muted'}`}>
                  {count}
                </span>
                {!isFilterable && (
                  <span className="text-[10px] eg-text-muted">(100+)</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Line Types Legend */}
        {edgeTypes.length > 0 && (
          <div className="p-4 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
            <h3 className="text-sm font-semibold eg-text-secondary uppercase tracking-wider mb-3">Line Types</h3>
            <div className="space-y-1.5">
              {edgeTypes.filter(s => s !== 'COMM').map(signal => (
                <div key={signal} className="flex items-center px-2 py-1">
                  <span
                    className="w-8 h-0.5 mr-2"
                    style={{ backgroundColor: getEdgeColor(signal) }}
                  ></span>
                  <span className="text-xs eg-text-secondary">{signal} Line</span>
                </div>
              ))}
              {edgeTypes.includes('COMM') && (
                <div className="flex items-center px-2 py-1">
                  <span
                    className="w-8 h-0.5 mr-2"
                    style={{
                      backgroundImage: `repeating-linear-gradient(90deg, ${getEdgeColor('COMM')}, ${getEdgeColor('COMM')} 4px, transparent 4px, transparent 6px)`
                    }}
                  ></span>
                  <span className="text-xs eg-text-secondary">COMM Line</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Power Components */}
        {powerComponents.length > 0 && (
          <div className="p-4 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
            <h3 className="text-sm font-semibold eg-text-secondary uppercase tracking-wider mb-1">Power Flow</h3>
            <p className="text-[11px] eg-text-muted mb-3">Click a component type to expand the graph to that level.</p>
            {renderComponentList(powerComponents)}
          </div>
        )}

        {/* Auxiliary Components */}
        {auxiliaryComponents.length > 0 && (
          <div className="p-4 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold eg-text-secondary uppercase tracking-wider">Auxiliary Systems</h3>
              <div className="flex gap-1.5">
                {onExpandAuxiliary && (
                  <button
                    onClick={onExpandAuxiliary}
                    className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded text-[11px] font-medium transition-colors border border-blue-500/40"
                    title="Show all auxiliary nodes and their connections"
                  >
                    <Eye size={11} />
                    Show
                  </button>
                )}
                {onCollapseAuxiliary && (
                  <button
                    onClick={onCollapseAuxiliary}
                    className="flex items-center gap-1 px-2 py-0.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] eg-text-secondary rounded text-[11px] font-medium transition-colors border border-[var(--color-border)]"
                    title="Hide all auxiliary nodes"
                  >
                    <EyeOff size={11} />
                    Hide
                  </button>
                )}
              </div>
            </div>
            {renderComponentList(auxiliaryComponents)}
          </div>
        )}

        {/* Extraction Report link */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'extractiondata' } }))}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-lg transition-colors border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]"
          style={{ backgroundColor: 'var(--color-surface)' }}
        >
          <FileCheck className="w-4 h-4 text-engramiq-green flex-shrink-0" />
          <span className="text-xs font-medium eg-text-secondary">Extraction Report</span>
          <ChevronRight className="w-3 h-3 eg-text-muted ml-auto" />
        </button>
      </div>
    );
  };

  // Helper to check if a string looks like a UUID
  const isUUID = (str: string): boolean => {
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidPattern.test(str);
  };

  const renderDetails = () => {
    if (selectedNodes.length === 0) return <div className="eg-text-muted italic p-4">No component selected.</div>;

    const primaryNode = selectedNodes[0];
    const isMultiSelect = selectedNodes.length > 1;

    // Filter out UUIDs from aliases - only keep valid human-readable aliases
    const validAliases = !isMultiSelect && primaryNode.details.Aliases
      ? primaryNode.details.Aliases.filter((alias: string) => !isUUID(alias))
      : [];

    // Handle row click for downstream hierarchy - toggle expand/collapse
    const handleHierarchyRowClick = (type: string, isVisible: boolean) => {
      const nodeIds = selectedNodes.map(n => n.id);
      if (isVisible) {
        // All visible - collapse this type and everything below
        if (onCollapseTypeAndBelow) {
          onCollapseTypeAndBelow(nodeIds, type);
        }
      } else {
        // Not all visible - expand to show this type
        if (onToggleType) {
          onToggleType(nodeIds, type);
        }
      }
    };

    return (
      <div className="space-y-6">
        {!isMultiSelect && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Type</label>
            <div className="text-emerald-400 font-mono mt-1 break-words">
              {primaryNode.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </div>
          </div>
        )}

        {/* Power Flow Aggregates - from configuration.aggregate_specs */}
        {!isMultiSelect && primaryNode.details.configuration?.aggregate_specs && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Power Flow Aggregates</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(primaryNode.details.configuration.aggregate_specs as any).stc_power_w !== undefined && (
                <div className="bg-[var(--color-surface)] rounded px-3 py-2 border border-[var(--color-border)]">
                  <div className="text-[10px] eg-text-muted">STC Power (W)</div>
                  <div className="text-sm font-mono text-emerald-400">
                    {(primaryNode.details.configuration.aggregate_specs as any).stc_power_w.toLocaleString()}
                  </div>
                </div>
              )}
              {(primaryNode.details.configuration.aggregate_specs as any).module_count !== undefined && (
                <div className="bg-[var(--color-surface)] rounded px-3 py-2 border border-[var(--color-border)]">
                  <div className="text-[10px] eg-text-muted">Modules</div>
                  <div className="text-sm font-mono text-emerald-400">
                    {(primaryNode.details.configuration.aggregate_specs as any).module_count}
                  </div>
                </div>
              )}
              {(() => {
                const specs = primaryNode.details.configuration.aggregate_specs as any;
                const acEdgeCount = links.filter(l => {
                  const src = typeof l.source === 'object' ? (l.source as any).id : l.source;
                  const tgt = typeof l.target === 'object' ? (l.target as any).id : l.target;
                  return (src === primaryNode.id || tgt === primaryNode.id) && l.details?.signal === 'AC';
                }).length;
                const hasTwoAcEdges = acEdgeCount >= 2;

                if (hasTwoAcEdges) {
                  // Nodes with 2+ AC edges: don't show Isc/Voc or AC current/voltage
                  return null;
                }

                // Default: show Isc and Voc
                return (
                  <>
                    {specs.isc_a !== undefined && (
                      <div className="bg-[var(--color-surface)] rounded px-3 py-2 border border-[var(--color-border)]">
                        <div className="text-[10px] eg-text-muted">I<sub>sc</sub> (A)</div>
                        <div className="text-sm font-mono text-emerald-400">
                          {specs.isc_a.toFixed(2)}
                        </div>
                      </div>
                    )}
                    {specs.voc_v !== undefined && (
                      <div className="bg-[var(--color-surface)] rounded px-3 py-2 border border-[var(--color-border)]">
                        <div className="text-[10px] eg-text-muted">V<sub>oc</sub> (V)</div>
                        <div className="text-sm font-mono text-emerald-400">
                          {specs.voc_v.toFixed(1)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Aliases - always shown */}
        {!isMultiSelect && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Aliases</label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {validAliases.length > 0 ? (
                validAliases.map((alias: string, idx: number) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs font-mono border border-blue-500/30"
                  >
                    {alias}
                  </span>
                ))
              ) : (
                <span className="eg-text-muted text-sm italic">None</span>
              )}
            </div>
          </div>
        )}

        {/* Downstream Hierarchy - moved above Power Distribution */}
        {electricalStats && electricalStats.length > 0 && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Downstream Hierarchy</label>
            <div className="mt-2 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-[var(--color-table-header)] eg-text-primary">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">{isMultiSelect ? 'Count/Per' : 'Count'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-table-border)]">
                  {electricalStats.map(({ type, count }) => {
                    const isVisible = checkVisible(type);
                    return (
                      <tr
                        key={type}
                        className={`cursor-pointer transition-colors ${isVisible ? 'hover:bg-red-500/10' : 'hover:bg-emerald-500/10'}`}
                        onClick={() => handleHierarchyRowClick(type, isVisible)}
                        title={isVisible ? `Click to hide ${type.replace(/_/g, ' ')} and below` : `Click to show ${type.replace(/_/g, ' ')}`}
                      >
                        <td className="px-3 py-2 flex items-center eg-text-secondary">
                          <span
                            className="w-2 h-2 rounded-full mr-2"
                            style={{ backgroundColor: ComponentColors[type] || '#9CA3AF' }}
                          ></span>
                          {type.replace(/_/g, ' ')}
                          {isVisible && (
                            <svg className="w-3 h-3 ml-2 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-400">
                          {isMultiSelect ? `${count}/${Math.round(count / selectedNodes.length)}` : count}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Power Distribution Table */}
        {powerDistribution && powerDistribution.length > 0 && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">
              Site Distribution - {Array.from(new Set(selectedNodes.map(n => n.type))).map(t => t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).join(', ')}
            </label>
            <div className="mt-2 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
              <table className="w-full text-xs text-left">
                <thead className="bg-[var(--color-table-header)] eg-text-primary">
                  <tr>
                    <th className="px-3 py-2">Power (kW)</th>
                    <th className="px-3 py-2 text-right">Count</th>
                    <th className="px-3 py-2 text-right">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-table-border)]">
                  {powerDistribution.map(({ power, count, percentage, nodeIds }) => (
                    <tr
                      key={power}
                      className="hover:bg-[var(--color-table-row-hover)] cursor-pointer transition-colors"
                      onClick={() => {
                        if (onMultiNodeSelect) {
                          onMultiNodeSelect(nodeIds);
                        }
                      }}
                      title={`Select all ${count} components with ${(power / 1000).toFixed(2)} kW`}
                    >
                      <td className="px-3 py-2 eg-text-secondary font-mono">
                        {(power / 1000).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-400">{count}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-400">
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* STC Power & Module Distribution */}
        {(stcPowerDistribution || moduleCountDistribution) && (
          <div>
            <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">
              Configuration Distribution - {Array.from(new Set(selectedNodes.map(n => n.type))).map(t => t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')).join(', ')}
            </label>
            <div className="mt-2 space-y-3">
              {/* STC Power Distribution */}
              {stcPowerDistribution && stcPowerDistribution.length > 0 && (
                <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
                  <div className="bg-[var(--color-overlay-subtle)] px-3 py-1.5 border-b border-[var(--color-border)]">
                    <span className="text-[10px] eg-text-secondary uppercase tracking-wider">By STC Power</span>
                  </div>
                  <table className="w-full text-xs text-left">
                    <thead className="bg-[var(--color-table-header)] eg-text-primary">
                      <tr>
                        <th className="px-3 py-2">STC Power (kW)</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-table-border)]">
                      {stcPowerDistribution.map(({ stcPower, count, percentage, nodeIds }) => (
                        <tr
                          key={stcPower}
                          className="hover:bg-[var(--color-table-row-hover)] cursor-pointer transition-colors"
                          onClick={() => {
                            if (onMultiNodeSelect) {
                              onMultiNodeSelect(nodeIds);
                            }
                          }}
                          title={`Select all ${count} components with ${(stcPower / 1000).toFixed(2)} kW STC`}
                        >
                          <td className="px-3 py-2 eg-text-secondary font-mono">
                            {(stcPower / 1000).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-400">{count}</td>
                          <td className="px-3 py-2 text-right font-mono text-blue-400">
                            {percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Module Count Distribution */}
              {moduleCountDistribution && moduleCountDistribution.length > 0 && (
                <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
                  <div className="bg-[var(--color-overlay-subtle)] px-3 py-1.5 border-b border-[var(--color-border)]">
                    <span className="text-[10px] eg-text-secondary uppercase tracking-wider">By Module Count</span>
                  </div>
                  <table className="w-full text-xs text-left">
                    <thead className="bg-[var(--color-table-header)] eg-text-primary">
                      <tr>
                        <th className="px-3 py-2">Modules</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-table-border)]">
                      {moduleCountDistribution.map(({ moduleCount, count, percentage, nodeIds }) => (
                        <tr
                          key={moduleCount}
                          className="hover:bg-[var(--color-table-row-hover)] cursor-pointer transition-colors"
                          onClick={() => {
                            if (onMultiNodeSelect) {
                              onMultiNodeSelect(nodeIds);
                            }
                          }}
                          title={`Select all ${count} components with ${moduleCount} modules`}
                        >
                          <td className="px-3 py-2 eg-text-secondary font-mono">
                            {moduleCount}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-400">{count}</td>
                          <td className="px-3 py-2 text-right font-mono text-blue-400">
                            {percentage.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {renderProperties('properties')}
        {renderProperties('configuration')}

        {/* Edge Connections */}
        {!isMultiSelect && (() => {
          const nodeId = primaryNode.id;
          const incomingEdges = links.filter(l => l.target === nodeId);
          const outgoingEdges = links.filter(l => l.source === nodeId);

          const getEdgeColor = (signal: string) => {
            if (signal === 'AC') return '#ef4444';
            if (signal === 'DC') return '#10b981';
            if (signal === 'COMM') return '#3b82f6';
            return '#9CA3AF';
          };

          const renderEdgeList = (edges: typeof links, direction: 'incoming' | 'outgoing') => {
            if (edges.length === 0) {
              return <div className="mt-1 eg-text-secondary text-sm italic">None</div>;
            }

            // Keys to hide from display
            const hiddenKeys = ['pref', 'wire_class'];

            return (
              <div className="mt-2 space-y-3">
                {edges.map((edge, idx) => {
                  const otherNodeId = direction === 'incoming' ? edge.source : edge.target;
                  const otherNode = nodes.find(n => n.id === otherNodeId);
                  const config = edge.details.configuration as Record<string, any> | undefined;
                  const wireClass = config?.wire_class || config?.['Wire Class'] || config?.Wire_Class || config?.wireClass || '';
                  const isMainWire = wireClass.toString().toLowerCase() === 'main';

                  // Filter properties and config to exclude hidden keys and objects
                  const visibleProps = edge.details.properties
                    ? Object.entries(edge.details.properties).filter(([key]) => !hiddenKeys.includes(key.toLowerCase()))
                    : [];
                  const visibleConfig = config
                    ? Object.entries(config).filter(([key, value]) =>
                        !hiddenKeys.includes(key.toLowerCase()) &&
                        typeof value !== 'object'
                      )
                    : [];

                  return (
                    <div
                      key={idx}
                      className={`rounded-lg p-4 border cursor-pointer transition-all duration-150 hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20 ${
                        isMainWire
                          ? 'bg-[var(--color-surface-active)] border-[var(--color-border-secondary)] hover:border-emerald-500/50 hover:bg-[var(--color-surface-hover)]'
                          : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-border-secondary)] hover:bg-[var(--color-surface-hover)]'
                      }`}
                      onClick={() => otherNode && onMultiNodeSelect && onMultiNodeSelect([otherNodeId])}
                      title={`Click to select ${otherNodeId}`}
                    >
                      {/* Edge header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="w-8 h-0.5 rounded-full"
                          style={{
                            backgroundColor: edge.details.signal === 'COMM' ? 'transparent' : getEdgeColor(edge.details.signal),
                            backgroundImage: edge.details.signal === 'COMM'
                              ? `repeating-linear-gradient(90deg, ${getEdgeColor('COMM')}, ${getEdgeColor('COMM')} 4px, transparent 4px, transparent 6px)`
                              : 'none'
                          }}
                        ></span>
                        <span className="text-sm font-semibold" style={{ color: getEdgeColor(edge.details.signal) }}>
                          {edge.details.signal}
                        </span>
                        {wireClass && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${isMainWire ? 'bg-emerald-600/30 text-emerald-400' : 'bg-[var(--color-surface)] eg-text-secondary'}`}>
                            {wireClass}
                          </span>
                        )}
                        <span className="text-sm eg-text-secondary">→</span>
                        <span className="text-sm eg-text-primary truncate flex-1 font-medium">
                          {otherNodeId}
                        </span>
                        {otherNode && (
                          <span className="text-xs eg-text-secondary">
                            ({otherNode.type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')})
                          </span>
                        )}
                      </div>

                      {/* Properties */}
                      {visibleProps.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                          <div className="text-xs eg-text-secondary mb-2 font-medium">Properties</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {visibleProps.map(([key, value]) => (
                              <span key={key} className="text-xs">
                                <span className="eg-text-secondary">{key}:</span> <span className="text-emerald-400 font-mono">{String(value)}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Configuration */}
                      {visibleConfig.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                          <div className="text-xs eg-text-secondary mb-2 font-medium">Configuration</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {visibleConfig.map(([key, value]) => (
                              <span key={key} className="text-xs">
                                <span className="eg-text-secondary">{key}:</span> <span className="text-emerald-400 font-mono">{String(value)}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          };

          return (
            <>
              <div>
                <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">
                  Incoming Edges ({incomingEdges.length})
                </label>
                {renderEdgeList(incomingEdges, 'incoming')}
              </div>

              <div>
                <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">
                  Outgoing Edges ({outgoingEdges.length})
                </label>
                {renderEdgeList(outgoingEdges, 'outgoing')}
              </div>
            </>
          );
        })()}
      </div>
    );
  };

  const renderConnections = () => {
    if (selectedNodes.length === 0) return <div className="eg-text-muted italic p-4">No component selected.</div>;

    const isMultiSelect = selectedNodes.length > 1;

    // For multi-select, show a simple message
    if (isMultiSelect) {
      return (
        <div className="space-y-4">
          <p className="eg-text-secondary italic text-sm">
            Select a single component to view the path to grid.
          </p>
        </div>
      );
    }

    // Get edge color
    const getEdgeColor = (signal: string) => {
      if (signal === 'AC') return '#ef4444';
      if (signal === 'DC') return '#10b981';
      if (signal === 'COMM') return '#3b82f6';
      return '#9CA3AF';
    };

    // Format type name
    const formatTypeName = (type: string) => {
      return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    if (!pathToGrid || pathToGrid.length === 0) {
      return <div className="eg-text-muted italic text-sm p-4">No path to grid found.</div>;
    }

    // Reverse path so grid is at top
    const pathFromGrid = [...pathToGrid].reverse();

    return (
      <div className="space-y-2">
        <label className="text-xs font-semibold eg-text-secondary uppercase tracking-wider">Path to Grid</label>

        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-3">
          {pathFromGrid.map((step, index) => {
            const isSelected = step.node.id === selectedNodes[0].id;
            const isRoot = index === 0;
            const nextStep = index < pathFromGrid.length - 1 ? pathFromGrid[index + 1] : null;

            // Edge details (the edge going DOWN to the next node)
            const edgeToNext = nextStep?.edge;
            const edgeSignal = edgeToNext?.details.signal || '';

            // Get wire configuration
            const config = edgeToNext?.details.configuration as Record<string, any> | undefined;
            const wireClass = config?.wire_class || config?.['Wire Class'] || config?.Wire_Class || config?.wireClass || '';
            const wireClassLower = wireClass.toString().toLowerCase();
            const isMainWire = wireClassLower === 'main';
            const isStubWire = wireClassLower === 'stub';

            // Get length - prefer configuration, fallback to properties
            const edgeLengthFt = Number(config?.['Length (ft)'] || config?.['length_ft'] || 0);
            const edgeLengthM = Number(edgeToNext?.details.properties?.['Cable Length (m)'] ||
                                       edgeToNext?.details.properties?.['Length'] ||
                                       edgeToNext?.details.properties?.['cable_length'] || 0);
            const edgeLength = edgeLengthFt > 0 ? edgeLengthFt : edgeLengthM;
            const lengthUnit = edgeLengthFt > 0 ? 'ft' : 'm';

            // Get power/voltage drop from configuration
            const voltageDrop = Number(config?.['Voltage Drop (%)'] || config?.['voltage_drop_pct'] || 0);
            const powerLoss = Number(edgeToNext?.details.properties?.['Power Loss (W)'] ||
                                     edgeToNext?.details.properties?.['Loss'] ||
                                     edgeToNext?.details.properties?.['power_loss'] || 0);

            return (
              <div key={step.node.id}>
                {/* Node */}
                <div
                  className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${isSelected ? 'bg-emerald-500/20 border border-emerald-500/40' : 'hover:bg-[var(--color-surface-hover)]'}`}
                  onClick={() => onMultiNodeSelect && onMultiNodeSelect([step.node.id])}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Symbol */}
                  <svg width="24" height="24" viewBox="-20 -20 40 40" className="flex-shrink-0">
                    <path
                      d={getSymbol(step.node.type)}
                      fill={step.node.color}
                      stroke={step.node.color}
                      strokeWidth="2"
                      opacity="0.9"
                    />
                  </svg>

                  {/* Node Info */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium truncate ${isSelected ? 'text-emerald-400' : 'eg-text-primary'}`}>
                      {step.node.id}
                    </div>
                    <div className="text-[10px] eg-text-muted">
                      {formatTypeName(step.node.type)}
                    </div>
                  </div>

                  {/* Cumulative Stats */}
                  <div className="text-right flex-shrink-0">
                    {step.cumulativePower > 0 && (
                      <div className="text-xs font-mono text-emerald-400">
                        {(step.cumulativePower / 1000).toFixed(1)} kW
                      </div>
                    )}
                    {!isRoot && step.cumulativeLength > 0 && (
                      <div className="text-[10px] font-mono eg-text-muted">
                        Sum {step.cumulativeLength.toFixed(1)}m
                      </div>
                    )}
                  </div>
                </div>

                {/* Edge connector (if not last) */}
                {nextStep && (
                  <div className="flex items-stretch ml-[11px] my-0.5">
                    {/* Vertical line - dashed for stub, solid for main */}
                    <div
                      className="w-0.5 min-h-[28px]"
                      style={{
                        backgroundColor: isStubWire ? 'transparent' : getEdgeColor(edgeSignal),
                        backgroundImage: isStubWire
                          ? `repeating-linear-gradient(180deg, ${getEdgeColor(edgeSignal)}, ${getEdgeColor(edgeSignal)} 3px, transparent 3px, transparent 6px)`
                          : 'none'
                      }}
                    />

                    {/* Edge details */}
                    <div className="ml-3 flex flex-col gap-0.5 text-[10px] eg-text-muted">
                      <span className="font-medium" style={{ color: getEdgeColor(edgeSignal) }}>
                        {edgeSignal || '—'}
                      </span>
                      <div className="flex items-center gap-3">
                        {edgeLength > 0 && (
                          <span className="eg-text-secondary">
                            {edgeLength.toFixed(1)} {lengthUnit}
                          </span>
                        )}
                        {voltageDrop > 0 && (
                          <span className="text-amber-400">-{voltageDrop.toFixed(2)}%</span>
                        )}
                        {powerLoss > 0 && (
                          <span className="text-red-400">-{(powerLoss / 1000).toFixed(2)}kW</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        {pathToGrid.length > 1 && (
          <div className="mt-3 p-2 bg-[var(--color-overlay-subtle)] rounded border border-[var(--color-border)] text-xs eg-text-secondary">
            <div className="flex justify-between">
              <span>Total Path Length:</span>
              <span className="font-mono eg-text-primary">
                {pathToGrid[pathToGrid.length - 1].cumulativeLength.toFixed(1)}m
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Hops to Grid:</span>
              <span className="font-mono eg-text-primary">{pathToGrid.length - 1}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const primaryNode = selectedNodes[0];
  const isMultiSelect = selectedNodes.length > 1;

  return (
    <div
      className={`relative h-full border-l border-[var(--color-border)] transition-all duration-300 ease-in-out flex flex-col z-20 ${isOpen ? 'w-96 translate-x-0' : 'w-0 translate-x-full border-none'}`}
      style={{
        background: 'var(--color-bg-primary)',
        backdropFilter: 'blur(16px) saturate(120%)',
        WebkitBackdropFilter: 'blur(16px) saturate(120%)',
        boxShadow: '-4px 0 32px 0 rgba(0, 0, 0, 0.4)'
      }}
    >
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        className="absolute -left-8 top-1/2 -translate-y-1/2 border border-[var(--color-border)] border-r-0 rounded-l-md p-1 eg-text-secondary hover:eg-text-primary shadow-lg z-30 w-8 h-12 flex items-center justify-center transition-colors backdrop-blur-lg bg-[var(--color-surface)]"
        title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
      >
        {isOpen ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </button>

      {/* Content Container */}
      <div className="flex-1 overflow-hidden w-96 flex flex-col bg-[var(--color-bg-primary)]">

        {/* Header Section */}
        <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-bg-primary)]">

          {/* Top Bar: Title & Close */}
          <div className="p-4 flex justify-between items-center">
            <div className="truncate pr-2 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold eg-text-primary truncate" title={primaryNode?.id || "Graph Explorer"}>
                  {selectedNodes.length === 0 ? "Graph Explorer" : (isMultiSelect ? `${selectedNodes.length} Items Selected` : primaryNode.id)}
                </h2>
                {/* Page Reference Chip */}
                {nodePageRef !== null && !isMultiSelect && (
                  <button
                    onClick={() => navigateToAsBuiltPage(nodePageRef, primaryNode?.id, primaryNode?.id)}
                    className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded-full text-xs font-medium transition-colors border border-emerald-500/30 hover:border-emerald-500/50 flex-shrink-0"
                    title={`View on page ${nodePageRef} in As-Built drawings`}
                  >
                    <FileText size={12} />
                    p{nodePageRef}
                  </button>
                )}
              </div>
              {isMultiSelect && selectedTypeCounts && (
                <div className="text-xs eg-text-secondary mt-1 space-y-0.5">
                  {selectedTypeCounts.map(({ type, count }) => (
                    <div key={type} className="flex items-center gap-1">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: ComponentColors[type as keyof typeof ComponentColors] || '#9CA3AF' }}
                      ></span>
                      <span>{count}× {type.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedNodes.length > 0 && (
              <button onClick={onClose} className="eg-text-secondary hover:eg-text-primary focus:outline-none shrink-0 p-1 hover:bg-[var(--color-surface-hover)] rounded transition-colors">
                <X size={18} />
              </button>
            )}
          </div>

          {/* Controls Bar */}
          <div className="px-4 pb-4 flex gap-2">
            <button
              onClick={() => onOrientationChange(orientation === 'TD' ? 'LR' : 'TD')}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)] eg-text-secondary hover:eg-text-primary rounded text-xs font-medium transition-colors border border-[var(--color-border)]"
              title={`Switch to ${orientation === 'TD' ? 'horizontal' : 'vertical'} layout`}
            >
              <RotateCcw size={14} />
              {orientation === 'TD' ? 'Horizontal' : 'Vertical'}
            </button>
          </div>

          {/* Node Search */}
          <div ref={searchRef} className="px-4 pb-3 relative">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 eg-text-secondary pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={nodeSearchTerm}
                onChange={(e) => { setNodeSearchTerm(e.target.value); setHighlightedIndex(-1); }}
                onFocus={() => setSearchFocusCount(c => c + 1)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchFocusCount(0);
                    setNodeSearchTerm('');
                    setHighlightedIndex(-1);
                    (e.target as HTMLInputElement).blur();
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightedIndex(i => {
                      const next = Math.min(i + 1, searchResults.length - 1);
                      requestAnimationFrame(() => {
                        dropdownRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
                      });
                      return next;
                    });
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightedIndex(i => {
                      const next = Math.max(i - 1, 0);
                      requestAnimationFrame(() => {
                        dropdownRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
                      });
                      return next;
                    });
                  } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                    e.preventDefault();
                    onNodeSelect?.(searchResults[highlightedIndex].id);
                    setNodeSearchTerm('');
                    setHighlightedIndex(-1);
                    requestAnimationFrame(() => searchInputRef.current?.focus());
                  }
                }}
                placeholder="Search nodes..."
                className="w-full pl-8 pr-3 py-1.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs eg-text-primary placeholder-[var(--color-input-placeholder)] focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-colors"
              />
            </div>
            {isSearchFocused && nodeSearchTerm.trim() && (
              <div ref={dropdownRef} className="absolute left-4 right-4 top-full mt-1 bg-[var(--color-dropdown-bg)] border border-[var(--color-border)] rounded shadow-xl z-50 max-h-64 overflow-y-auto custom-scroll">
                {searchResults.length === 0 ? (
                  <div className="px-3 py-2 text-xs eg-text-muted">No nodes found</div>
                ) : (
                  searchResults.map((node, idx) => (
                    <button
                      key={node.id}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${idx === highlightedIndex ? 'bg-[var(--color-surface-active)] eg-text-primary' : 'hover:bg-[var(--color-surface-hover)]'}`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent blur before click fires
                        onNodeSelect?.(node.id);
                        setNodeSearchTerm('');
                        setHighlightedIndex(-1);
                        // Re-focus input after re-render so user can immediately search again
                        requestAnimationFrame(() => searchInputRef.current?.focus());
                      }}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: node.color }}
                      />
                      <span className="eg-text-primary truncate font-medium">{node.id}</span>
                      <span className="eg-text-muted truncate ml-auto">{node.type.replace(/_/g, ' ')}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Tabs Bar */}
          <div className="flex border-t border-[var(--color-border)]">
            <button
              onClick={() => setActiveTab('legend')}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'legend' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent eg-text-secondary hover:bg-[var(--color-surface-hover)]'}`}
            >
              <List size={14} />
              Legend
            </button>
            <button
              onClick={() => setActiveTab('details')}
              disabled={selectedNodes.length === 0}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'details' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent eg-text-secondary hover:bg-[var(--color-surface-hover)]'} ${selectedNodes.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Info size={14} />
              Details
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              disabled={selectedNodes.length === 0}
              className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'connections' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' : 'border-transparent eg-text-secondary hover:bg-[var(--color-surface-hover)]'} ${selectedNodes.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <GitBranch size={14} />
              Path
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scroll bg-[var(--color-bg-primary)]">
          {activeTab === 'legend' && renderLegend()}
          {activeTab === 'details' && renderDetails()}
          {activeTab === 'connections' && renderConnections()}
        </div>
      </div>
    </div>
  );
};

export default DetailsPanel;
